/*
  <fw-scroll-fill> — scroll-driven character fill
  ------------------------------------------------
  Splits its heading text into per-character spans, then sweeps a soft "head"
  across them as the element scrolls up through the viewport. Each character
  gets a --t value (0..1) that CSS uses to interpolate base -> fill colour, so
  the fill is a smooth gradient sweep (not a hard per-char on/off). Reverses on
  scroll up.

  Markup contract (rendered by Liquid):
    <fw-scroll-fill>
      <h2 class="fw-scroll-fill__text">...text...</h2>
    </fw-scroll-fill>

  Colours come from CSS (--fw-empty / --fw-fill, set from section settings).
  Scoped to `this` (per instance), respects prefers-reduced-motion.
  Self-contained, namespaced, registered once.
*/
(function () {
  if (customElements.get('fw-scroll-fill')) return;

  var RAMP = 16; // how many characters are mid-transition at once (bigger = softer)
  var PIN_MIN_WIDTH = 990; // must match the CSS breakpoint where the pin engages
  var FILL_END = 0.85; // text reaches 100% at this fraction of the pin scroll,
  // leaving a readable beat of fully-filled text before the next section covers it

  customElements.define(
    'fw-scroll-fill',
    class FwScrollFill extends HTMLElement {
      connectedCallback() {
        this._text = this.querySelector('.fw-scroll-fill__text');
        if (!this._text) return;

        // When inside a pin track (desktop sticky mode), progress is driven by
        // how far we've scrolled through the track, not by this element's own
        // travel — a pinned element doesn't move.
        this._track = this.closest('.fw-scroll-fill-track');

        this._buildChars();
        this._n = this._chars.length;

        var reduce =
          window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (reduce) {
          this._chars.forEach(function (c) {
            c.style.setProperty('--t', '1');
          });
          return;
        }

        this._ticking = false;
        this._update = this._update.bind(this);
        this._onScroll = function () {
          if (!this._ticking) {
            this._ticking = true;
            window.requestAnimationFrame(this._update);
          }
        }.bind(this);

        this._update();
        window.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._onScroll, { passive: true });
      }

      disconnectedCallback() {
        if (this._onScroll) {
          window.removeEventListener('scroll', this._onScroll);
          window.removeEventListener('resize', this._onScroll);
        }
      }

      _buildChars() {
        // Split into words (kept unbreakable) then characters.
        var parts = this._text.textContent.split(/(\s+)/);
        this._text.textContent = '';
        parts.forEach(
          function (part) {
            if (/^\s+$/.test(part)) {
              this._text.appendChild(document.createTextNode(part));
              return;
            }
            var word = document.createElement('span');
            word.className = 'fw-scroll-fill__word';
            for (var i = 0; i < part.length; i++) {
              var c = document.createElement('span');
              c.className = 'fw-scroll-fill__char';
              c.textContent = part[i];
              word.appendChild(c);
            }
            this._text.appendChild(word);
          }.bind(this)
        );
        this._chars = Array.prototype.slice.call(
          this._text.querySelectorAll('.fw-scroll-fill__char')
        );
      }

      _update() {
        this._ticking = false;
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var p;

        if (this._track && window.innerWidth >= PIN_MIN_WIDTH) {
          // Pinned mode: progress = distance scrolled into the track / pin
          // distance (track height - viewport). Finishes at FILL_END.
          var tr = this._track.getBoundingClientRect();
          var dist = tr.height - vh;
          p = dist > 0 ? -tr.top / dist : 0;
          p = p / FILL_END;
        } else {
          // Legacy mode (mobile / no track): fill as the block travels up
          // through the viewport.
          var r = this.getBoundingClientRect();
          p = (vh * 0.82 - r.top) / (vh * 0.82 - vh * 0.18 + r.height);
        }

        p = Math.max(0, Math.min(1, p));
        var head = p * (this._n + RAMP);
        for (var i = 0; i < this._n; i++) {
          var t = (head - i) / RAMP;
          t = t < 0 ? 0 : t > 1 ? 1 : t;
          t = t * t * (3 - 2 * t); // smoothstep easing
          this._chars[i].style.setProperty('--t', t.toFixed(3));
        }
      }
    }
  );
})();
